import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import useCollection from 'Hooks/useCollection';
import MetaCollectionFormDialog from 'Components/UI/Meta/MetaForm/MetaCollectionFormDialog';

import metaOverrideSetMap from 'Resources';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';

function EditDialog({
  collection,
  resourceURL,
  metaOverrideSetName,
  onClose,
  onDelete,
  onFailure,
  onSubmit,
  refetchResource,
  ...rest
}) {
  const {
    collection: coll,
    setCollection: setColl,
  } = useCollection();

  useEffect(() => {
    async function loadCollection() {
      let newColl = collection.copy();

      // Filter the collection down to a single resource
      newColl.resources().filterItems(resource => resource.link('self')?.url() === resourceURL);

      if (refetchResource) {
        const [collResource] = newColl;
        try {
          newColl = await collResource.link('self').get();
        } catch (err) {
          displayErrorMessage(err, err.errors);
          return;
        }
      }

      const [resource] = newColl;

      resource.resetMeta();

      // Override meta on the collection if present
      if (metaOverrideSetName && metaOverrideSetMap[metaOverrideSetName]) {
        newColl = newColl.overrideMeta(metaOverrideSetMap[metaOverrideSetName]);
      }

      setColl(newColl);
    }
    loadCollection();
  }, []);

  if (!coll) {
    return null;
  }

  const handleDelete = (onDelete) ? (res) => {
    // Passing the current collection to onDelete because some delete endpoints return 204s instead of the deleted resource,
    // and it could be useful to know which resource was just deleted
    onDelete(res, coll);
    onClose();
  } : null;

  return (
    <MetaCollectionFormDialog
      open
      onClose={onClose}
      onDelete={handleDelete}
      onSubmit={(res) => {
        if (onSubmit) {
          onSubmit(res);
        }
        onClose();
      }}
      onFailure={(err) => {
        if (onFailure) {
          onFailure(err);
        }
      }}
      collection={coll}
      title="Edit Resource"
      {...rest}
    />
  );
}

EditDialog.defaultProps = {
  metaOverrideSetName: null,
  onDelete: null,
  onFailure: null,
  onSubmit: null,
  refetchResource: false,
};

EditDialog.propTypes = {
  collection: PropTypes.object.isRequired,
  resourceURL: PropTypes.string.isRequired,
  metaOverrideSetName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onFailure: PropTypes.func,
  onSubmit: PropTypes.func,
  refetchResource: PropTypes.bool,
};

export default EditDialog;
